/**
 * Read-only beneficiary reference card for the SessionPage sidebar —
 * lets the operator see the beneficiary's identity/address/legal-entity
 * details plus BOTH membership-scoped and contact-scoped custom fields
 * while asking Q&A questions during the call, without leaving the
 * session (2026-09-16 backlog: "so when asking questions the operator
 * can also see what was specified in the beneficiary details").
 *
 * Two round-trips: the CampaignMembership (membership-scoped custom
 * fields + intake notes) then the full Contact (identity/address/legal
 * fields + contact-scoped custom fields). Renders nothing for
 * standalone/ad-hoc sessions (no membership) or on error — this is
 * supplementary context, not something the operator depends on to run
 * the call.
 */
import { useQuery } from "@tanstack/react-query";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SiteCards } from "@/components/SiteCards";
import { describesSamePlace, worksitesOf } from "@/components/worksites";
import { ApiPaths, api } from "@/lib/api";
import { formatTime } from "@/lib/format";
import { displayPhone } from "@/lib/phone";
import { fetchSessionParticipants } from "@/modules/dsr";

import { SessionJoinLinkCard } from "./SessionJoinLinkCard";
import { MembershipFieldsCard, resolveFieldSchema } from "@/modules/campaigns";
import type { CampaignMembership, Contact } from "@/lib/types";

type Props = {
  campaignId: string;
  membershipId: string;
  /** When given, the beneficiary's join status + copyable link render
   *  inside this card instead of in a separate one. Redesign 2026-09-23
   *  — the operator had to reconcile three boxes to answer one question:
   *  is my beneficiary here, and if not how do I get them in? */
  sessionId?: string;
  onInvite?: () => void;
  /** Whether the disclosure is open by default. Defaults to true —
   *  unlike the Participants panel, this one exists specifically so the
   *  operator sees it without an extra click while asking questions. */
  defaultOpen?: boolean;
};

export function BeneficiaryContextPanel({
  campaignId,
  membershipId,
  sessionId,
  onInvite,
  defaultOpen = true,
}: Props) {
  const { t } = useTranslation();

  // Same query key as SessionJoinLinkCard, so React Query serves both
  // from one request rather than double-fetching.
  const participants = useQuery({
    queryKey: ["session-participants", sessionId, "with-pending"],
    queryFn: () => fetchSessionParticipants(sessionId!, { includePending: true }),
    enabled: !!sessionId,
    staleTime: 4000,
  });

  // The beneficiary IS the field user, so their presence belongs on
  // their own card rather than in a separate participants box the
  // operator has to cross-reference.
  const fieldUsers = (participants.data ?? []).filter(
    (p) => !p.pending && p.role === "field",
  );
  const present = fieldUsers.find((p) => !p.left_at);
  const departed = [...fieldUsers]
    .filter((p) => !!p.left_at)
    .sort((a, b) => (a.left_at! < b.left_at! ? 1 : -1))[0];

  const joinStatus = !sessionId
    ? null
    : present
      ? {
          tone: "text-cvp-ok",
          label: t("session.beneficiaryInCall", {
            time: formatTime(present.joined_at),
            defaultValue: "In call since {{time}}",
          }),
        }
      : departed
        ? {
            tone: "text-nb-text-3",
            label: t("session.beneficiaryLeft", {
              time: formatTime(departed.left_at),
              defaultValue: "Left at {{time}}",
            }),
          }
        : {
            tone: "text-cvp-warn",
            label: t("session.beneficiaryWaiting", {
              defaultValue: "Waiting to join",
            }),
          };

  const membership = useQuery({
    queryKey: ["campaignLead", campaignId, membershipId],
    queryFn: async () =>
      (
        await api.get<CampaignMembership>(
          ApiPaths.campaignLead(campaignId, membershipId),
        )
      ).data,
    enabled: !!campaignId && !!membershipId,
  });

  const contactId = membership.data?.contact;
  const contact = useQuery({
    queryKey: ["contact", contactId],
    queryFn: async () =>
      (await api.get<Contact>(ApiPaths.contact(contactId!))).data,
    enabled: !!contactId,
  });

  const country = (contact.data?.country || "").toUpperCase();
  const contactSchema = useQuery({
    // `campaign` layers in this campaign's OWN applies_to="contact" fields
    // (distinct from org/system-wide ones) — without it, an operator-mapped
    // campaign-scoped contact field is silently dropped even though it's
    // stored on the Contact and shows on its detail page.
    queryKey: ["fields-schema", "contact", country, campaignId],
    queryFn: () =>
      resolveFieldSchema({ applies_to: "contact", country, campaign: campaignId }),
    enabled: !!contact.data,
  });

  const contactCustomRows = useMemo(() => {
    if (!contact.data) return [];
    const rows = contactSchema.data ?? [];
    return rows.filter(
      (fd) => (contact.data!.custom_fields_jsonb?.[fd.key] ?? "") !== "",
    );
  }, [contact.data, contactSchema.data]);

  if (!campaignId || !membershipId) return null;
  if (membership.isLoading || contact.isLoading) {
    return (
      <p className="px-1 text-xs text-nb-text-3">
        {t("session.beneficiaryDetailsLoading")}
      </p>
    );
  }
  if (membership.isError || contact.isError || !contact.data) return null;

  const c = contact.data;
  const personName = `${c.first_name} ${c.last_name}`.trim();
  const displayName = c.is_business ? c.legal_name || personName : personName;
  const addressParts = [
    c.address_line,
    [c.postal_code, c.city].filter(Boolean).join(" "),
    c.country,
  ].filter(Boolean);
  // The same value reaching a panel by two routes is still one fact.
  // Reported 2026-09-27 from a live render: the beneficiary's name,
  // address, email and phone were each printed twice — roughly a third
  // of the panel restating itself, pushing the capture list that grows
  // during the call below the fold.
  //
  // Suppressed only when they MATCH. A legal entity can trade under a
  // different name, and the company contact is often a different person
  // from the beneficiary — those are real facts and must still show.
  const same = (a?: string | null, b?: string | null) =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

  const addressLine = addressParts.join(", ");
  // The flat address columns are a two-way mirror of the structured
  // Address rows (docs: address is the source of truth), so for a
  // single-site beneficiary the "Street address" row and the site card
  // below it are the same address twice over.
  // Compared on the structured parts, not the formatted string: the two
  // renderings differ by a ", " versus a newline, which a string compare
  // read as two different addresses.
  const addressShownAsSite = worksitesOf(c.addresses).some((a) =>
    describesSamePlace(c, a),
  );

  const hasCompanyContact =
    c.is_business &&
    (c.company_contact_name ||
      // Only when it differs — otherwise "Company contact" repeats the
      // email and phone printed four lines above it.
      (c.company_contact_email && !same(c.company_contact_email, c.email)) ||
      (c.company_contact_phone && !same(c.company_contact_phone, c.phone)));

  return (
    <details
      className="group rounded-md border border-nb-border bg-slate-900/40"
      open={defaultOpen}
    >
      <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-nb-text-2 hover:bg-slate-800/40">
        <span className="flex items-center gap-2">
          {t("session.beneficiaryDetails")}
          {joinStatus && (
            <span
              className={`rounded bg-slate-800/60 px-1.5 py-0.5 text-[10px] font-normal normal-case tracking-normal ${joinStatus.tone}`}
            >
              {joinStatus.label}
            </span>
          )}
        </span>
        <svg
          className="h-3 w-3 transition-transform group-open:rotate-180"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M3 4.5l3 3 3-3" />
        </svg>
      </summary>
      {/* Taller than the old flat max-h-96 (24rem): during a call the
          operator reads this panel while talking, and 24rem showed so
          little that nearly everything needed scrolling.

          clamp rather than a bigger fixed height, so it can't regress
          either way — never shorter than it was, grows with the viewport
          on a normal screen, and stays capped on a very tall one so the
          panel doesn't run past the video beside it. The scroll stays;
          it just has less to do. */}
      <div className="max-h-[clamp(24rem,62vh,46rem)] space-y-2 overflow-y-auto border-t border-nb-border px-3 py-2 text-xs">
        {/* The join link belongs with the person it is addressed to. */}
        {sessionId && (
          <SessionJoinLinkCard
            sessionId={sessionId}
            onInvite={onInvite}
            // This card only renders for a campaign session, and there
            // the beneficiary IS the field user.
            canInviteFieldUser={false}
          />
        )}
        <div>
          <p className="font-medium text-nb-text">
            {displayName || "—"}
            {c.is_business && (
              <span className="ml-1.5 pill text-[10px]">
                {t("contacts.businessHeading")}
              </span>
            )}
          </p>
          {c.is_business && personName && !same(personName, displayName) && (
            <p className="text-nb-text-3">{personName}</p>
          )}
        </div>

        <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5">
          {c.email && (
            <>
              <dt className="text-nb-text-3">{t("contacts.col.email")}</dt>
              <dd>{c.email}</dd>
            </>
          )}
          {c.phone && (
            <>
              <dt className="text-nb-text-3">{t("contacts.col.phone")}</dt>
              <dd className="font-mono">{displayPhone(c.phone)}</dd>
            </>
          )}
          {addressParts.length > 0 && !addressShownAsSite && (
            <>
              <dt className="text-nb-text-3">{t("party.addressLine")}</dt>
              <dd>{addressLine}</dd>
            </>
          )}
          {c.is_business && c.registration_number && (
            <>
              <dt className="text-nb-text-3">{t("party.registrationNumber")}</dt>
              <dd className="font-mono">{c.registration_number}</dd>
            </>
          )}
          {c.is_business && c.vat_id && (
            <>
              <dt className="text-nb-text-3">{t("party.vatId")}</dt>
              <dd className="font-mono">{c.vat_id}</dd>
            </>
          )}
        </dl>

        {/* Reported 2026-09-23 — the sites were only in the beneficiary
            drawer, and the operator running the call (who needs to know
            WHICH site they are inspecting) never saw them here at all.
            Numbered so both surfaces refer to the same "site 2". */}
        {worksitesOf(c.addresses).length > 0 && (
          <div className="rounded-md border border-nb-border bg-nb-bg/40 p-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-nb-text-2">
              {t("sessionDetail.addresses", {
                count: worksitesOf(c.addresses).length,
              })}
            </p>
            <div className="mt-1">
              <SiteCards
                addresses={worksitesOf(c.addresses)}
                campaign={campaignId}
              />
            </div>
          </div>
        )}

        {hasCompanyContact && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-nb-text-3">
              {t("contacts.companyContactHeading")}
            </p>
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5">
              {c.company_contact_name && (
                <>
                  <dt className="text-nb-text-3">{t("contacts.companyContactName")}</dt>
                  <dd>{c.company_contact_name}</dd>
                </>
              )}
              {c.company_contact_email &&
                !same(c.company_contact_email, c.email) && (
                <>
                  <dt className="text-nb-text-3">{t("contacts.col.email")}</dt>
                  <dd>{c.company_contact_email}</dd>
                </>
              )}
              {c.company_contact_phone &&
                !same(c.company_contact_phone, c.phone) && (
                <>
                  <dt className="text-nb-text-3">{t("contacts.col.phone")}</dt>
                  <dd className="font-mono">{displayPhone(c.company_contact_phone)}</dd>
                </>
              )}
            </dl>
          </div>
        )}

        {membership.data?.notes_intake && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-nb-text-3">
              {t("leads.col.notesIntake")}
            </p>
            <p className="whitespace-pre-wrap text-nb-text-2">
              {membership.data.notes_intake}
            </p>
          </div>
        )}

        {membership.data && (
          <MembershipFieldsCard
            campaign={campaignId}
            membershipId={membershipId}
            values={membership.data.custom_fields_jsonb}
            canEdit={false}
          />
        )}

        {contactCustomRows.length > 0 && (
          <div>
            <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-nb-text-3">
              {t("contacts.customFieldsHeading")}
            </p>
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5">
              {contactCustomRows.map((fd) => (
                <Fragment key={fd.id}>
                  <dt className="text-nb-text-3">{fd.label}</dt>
                  <dd>{String(c.custom_fields_jsonb?.[fd.key] ?? "")}</dd>
                </Fragment>
              ))}
            </dl>
          </div>
        )}
      </div>
    </details>
  );
}
